import Button from '@orn/shared-web/components/Button/Button';
import Checkbox from '@orn/shared-web/components/Checkbox';
import Input from '@orn/shared-web/components/Input';
import { PhoneNumberInput, usePhoneNumberInput } from '@orn/shared-web/components/Input';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { IS_DEV } from '../../../env';
import { useUpdateUser } from '../../../hooks/mutations/useUpdateUser';
import { useCurrentUser } from '../../../hooks/queries/useCurrentUser';
import WizardContainer from '../WizardContainer';
import WizardTitle from '../WizardTitle';

import type { IWizardStepProps } from './types';
import type { LoggedInUser } from '../../../providers/AuthProvider';

interface IBaseUserInfoStepProps extends Omit<IWizardStepProps, 'onBack'> {
  user: LoggedInUser;
  phoneNumber: string;
  onUserUpdate: ({ phoneNumber }: { phoneNumber: string }) => Promise<void>;
}

export const BaseUserInfoStep = ({
  user,
  phoneNumber: initialPhoneNumber,
  onUserUpdate,
  onNext,
}: IBaseUserInfoStepProps) => {
  const fullName = user.name || user.firstName || '';
  const email = user.email;

  const { value: phoneNumber, validation, handleChange } = usePhoneNumberInput(initialPhoneNumber || '', true);

  const [smsConsentChecked, setSmsConsentChecked] = React.useState(false);

  const handleContinue = React.useCallback(async () => {
    if (!validation.isValid || !phoneNumber || !smsConsentChecked) {
      return;
    }

    try {
      const e164PhoneNumber = validation.e164Value || phoneNumber;
      await onUserUpdate({ phoneNumber: e164PhoneNumber });
      onNext();
    } catch (error) {
      logger.error('Failed to update phone number:', error);
    }
  }, [validation.isValid, validation.e164Value, onNext, onUserUpdate, phoneNumber, smsConsentChecked]);

  return (
    <WizardContainer step={1}>
      {/* Content */}
      <div className="flex flex-col gap-2">
        <WizardTitle title="Sign up" />

        {/* Form */}
        <div className="flex flex-col gap-4 pb-8 pt-0">
          {/* Full Name */}
          <Input label="Full name" value={fullName} isDisabled size="md" />

          {/* Email Address */}
          <Input label="Email address" value={email} isDisabled size="md" />

          {/* Phone Number */}
          <PhoneNumberInput value={phoneNumber} onChange={handleChange} isRequired validation={validation} />

          {/* SMS Consent, required for A2P 10DLC Compliance */}
          <Checkbox
            isSelected={smsConsentChecked}
            onChange={setSmsConsentChecked}
            size="sm"
            label={
              <span className="text-tertiary text-xs">
                By checking this box, you agree to receive transactional and informational SMS messages from Mantle
                about your account. You also agree to Mantle's{' '}
                <a
                  href="https://www.gomantle.com/tos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://www.gomantle.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
                . Message and data rates may apply. Reply STOP to opt out.
              </span>
            }
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex w-full flex-col gap-2">
        <Button
          onClick={handleContinue}
          isDisabled={!validation.isValid || !phoneNumber || !smsConsentChecked}
          size="xl"
          color="primary"
          className="w-full"
        >
          Continue
        </Button>
      </div>
    </WizardContainer>
  );
};

type IUserInfoStepProps = IWizardStepProps;

const UserInfoStep = (props: IUserInfoStepProps) => {
  const { user } = useCurrentUser();
  const [updateUser] = useUpdateUser();

  const handleUserUpdate = React.useCallback(
    async ({ phoneNumber }: { phoneNumber: string }) => {
      await updateUser({
        variables: {
          input: {
            phoneNumber,
          },
        },
      });
    },
    [updateUser]
  );

  if (!user) {
    return null;
  }

  const defaultPhoneNumber = IS_DEV ? '+14155550011' : '';
  const phoneNumber = user.phoneNumber || defaultPhoneNumber;

  return <BaseUserInfoStep user={user} phoneNumber={phoneNumber} onUserUpdate={handleUserUpdate} {...props} />;
};

export default UserInfoStep;
